import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Percent, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { today } from '@/lib/pricing';

type FeeRow = {
  id: string;
  rate_percent: string;
  effective_from: string;
  note: string;
  scheduled_by_email: string;
  announced_at: string | null;
  announced_to: number;
  in_force: boolean;
};

type FeeState = { current: FeeRow | null; upcoming: FeeRow | null; history: FeeRow[] };

const day = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });

const rate = (value: string) => `${parseFloat(value).toString()}%`;

export default function ProcessingFeeCard() {
  const queryClient = useQueryClient();
  const [feeRate, setFeeRate] = React.useState('');
  const [feeDate, setFeeDate] = React.useState('');
  const [feeNote, setFeeNote] = React.useState('');

  const fee = useQuery({
    queryKey: ['processing-fee'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/processing-fee/')).data as FeeState;
    },
  });

  const scheduleFee = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post('/global-admin/processing-fee/', {
        rate_percent: feeRate,
        effective_from: feeDate,
        note: feeNote,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processing-fee'] });
      setFeeRate(''); setFeeDate(''); setFeeNote('');
      toast.success('Scheduled. Every advisor has been told.');
    },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message),
  });

  const cancelFee = useMutation({
    mutationFn: async (id: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.delete(`/global-admin/processing-fee/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processing-fee'] });
      toast.success('Called off. Advisors have been told it is not happening.');
    },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message),
  });

  return (
    <Card data-testid="processing-fee-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Percent className="h-4 w-4" />
          Processing fee
        </CardTitle>
        <CardDescription>
          What BitSwan keeps out of an advisor's share of invoices billed
          through the AOC. It never changes what the client pays, and it is
          fixed on each invoice when that invoice is issued. Advisors are
          emailed the moment a change is scheduled and see it in their portal
          until it takes effect.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {fee.isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3" data-testid="processing-fee-current">
              <span className="text-3xl font-bold tracking-tight">
                {rate(fee.data?.current?.rate_percent ?? '0')}
              </span>
              <span className="text-sm text-muted-foreground">
                {fee.data?.current
                  ? `in force since ${day(fee.data.current.effective_from)}`
                  : 'no fee is being kept'}
              </span>
            </div>

            {fee.data?.upcoming && (
              <div
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm"
                data-testid="processing-fee-upcoming"
              >
                <span>
                  <strong>{rate(fee.data.upcoming.rate_percent)}</strong> from{' '}
                  {day(fee.data.upcoming.effective_from)} —{' '}
                  {fee.data.upcoming.announced_to > 0
                    ? `${fee.data.upcoming.announced_to} advisor(s) told`
                    : 'nobody was told, the mail did not go out'}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => cancelFee.mutate(fee.data!.upcoming!.id)}
                  disabled={cancelFee.isPending}
                  data-testid="processing-fee-cancel"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Call it off
                </Button>
              </div>
            )}

            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="fee-rate">New rate (%)</Label>
                <Input
                  id="fee-rate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-32"
                  value={feeRate}
                  onChange={(e) => setFeeRate(e.target.value)}
                  data-testid="processing-fee-rate"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fee-date">In force from</Label>
                <Input
                  id="fee-date"
                  type="date"
                  min={today()}
                  value={feeDate}
                  onChange={(e) => setFeeDate(e.target.value)}
                  data-testid="processing-fee-date"
                />
              </div>
              <div className="min-w-[16rem] flex-1 space-y-1.5">
                <Label htmlFor="fee-note">Note to advisors (optional)</Label>
                <Input
                  id="fee-note"
                  value={feeNote}
                  onChange={(e) => setFeeNote(e.target.value)}
                  data-testid="processing-fee-note"
                />
              </div>
              <Button
                onClick={() => scheduleFee.mutate()}
                disabled={scheduleFee.isPending || !feeRate || !feeDate}
                data-testid="processing-fee-schedule"
              >
                {scheduleFee.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Percent className="h-4 w-4 mr-2" />
                )}
                Schedule and tell advisors
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Invoices issued from that day carry the new rate. Pick today to change it at once. Every advisor is emailed either way.
            </p>

            {(fee.data?.history ?? []).length > 0 && (
              <table className="w-full text-sm" data-testid="processing-fee-history">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="py-1">From</th>
                    <th className="py-1">Rate</th>
                    <th className="py-1">Scheduled by</th>
                    <th className="py-1">Told</th>
                    <th className="py-1">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {(fee.data?.history ?? []).map((row) => (
                    <tr key={row.id} className="border-t">
                      <td className="py-2">{day(row.effective_from)}</td>
                      <td className="py-2 font-medium">{rate(row.rate_percent)}</td>
                      <td className="py-2 text-muted-foreground">{row.scheduled_by_email}</td>
                      <td className="py-2 text-muted-foreground">{row.announced_to}</td>
                      <td className="py-2 text-muted-foreground">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
